import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, PopupData, RadiobuttonItemInfo, RequestError, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject } from 'webapp/utils/common';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import BarLoaderComponent from 'webapp/components/bar-loader';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import BoardSourceMigrationRowComponent from 'webapp/components/board/source-migration-row';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Board from 'webapp/models-custom/board';
import type UserRecord from 'webapp/models/user';

import type { SourceListEntry } from 'webapp/services/model-manager/sources';

export class SourceMigrationMap {
	fromSource: SourceListEntry;
	@tracked targetSourceId?: number;

	constructor(fromSource: SourceListEntry) {
		this.fromSource = fromSource;
	}

	get isError() {
		return !this.targetSourceId;
	}

	get migrationMap(): MigrationMap {
		return {
			fromSpaceAccessId: +this.fromSource.value,
			toSpaceAccessId: this.targetSourceId,
		};
	}
}

export interface MigrationMap {
	fromSpaceAccessId: number;
	toSpaceAccessId?: number;
}

export interface MigrationResponse {
	board: Board;
	successfullyMigrated: string[];
	unsuccessfullyMigrated: string[];
}

export enum MigrationMode {
	CREATE_NEW_BOARD = 1,
	REPLACE_METRICS = 2,
	REMOVE_METRICS = 3,
}

export default class BoardPopupMigrationToolComponent extends PopupInnerModalComponent<PopupData.Board.MigrationTool> {
	@service('view-settings-manager')
	declare viewSettingsManagerService: Service.ViewSettingsManager;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked isMessageVisible: boolean;
	@tracked areMigrationMapsLoading: boolean;
	@tracked hideMessageNextTime = false;
	@tracked removeIncompatibleMetrics = false;
	@tracked selectedMigrationMode = MigrationMode.CREATE_NEW_BOARD;
	@tracked sourceMigrationMaps: SourceMigrationMap[] = [];

	viewSettingsNamePrefix = 'dbx_migrationToolMessage';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const savedValues =
			this.viewSettingsManagerService.load<{ isHidden: boolean }>(this.settingsName, true) ||
			{};
		this.isMessageVisible = !savedValues.isHidden;

		const GA3SourceTypeId = this.mmSourceTypesService.findModelByKey('GoogleAnalytics')?.id,
			allBoardGA3Sources = this.boardModel.sourcesList.filter(
				(sourceListEntry: SourceListEntry) => {
					return sourceListEntry.type === GA3SourceTypeId;
				},
			);

		allBoardGA3Sources.forEach((sourceListEntry) => {
			this.sourceMigrationMaps.push(new SourceMigrationMap(sourceListEntry));
		});

		this.areMigrationMapsLoading = true;
		this.requestService
			.api<APIData.Migrate.MappedSourcesResponse>(
				'GET',
				'/migrate/mapped-migrate-sources',
				null,
				{
					withoutPrefix: true,
				},
			)
			.then((reply) => {
				if (reply?.mappedSources) {
					const mappedSources = reply?.mappedSources;
					this.sourceMigrationMaps.forEach((sourceMigrationMap) => {
						mappedSources.forEach((mappedSource: APIData.Migrate.SourceMap) => {
							const sourceId = sourceMigrationMap.fromSource.value;
							if (mappedSource[sourceId]) {
								sourceMigrationMap.targetSourceId = mappedSource[sourceId];
							}
						});
					});
				}
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService))
			.finally(() => {
				this.areMigrationMapsLoading = false;
			});

		this.analyticsService.event(TrackingEvent.GA4_TRANSITION_POPUP_OPENED);
	}

	get settingsName(): string {
		return `${this.viewSettingsNamePrefix}-${this.accountService.id}`;
	}

	get migrationModeOptions(): RadiobuttonItemInfo[] {
		return [
			{
				value: MigrationMode.CREATE_NEW_BOARD,
				label: 'Keep existing dashboard and create new one only with compatible GA4 metrics',
				label2: 'The new duplicated dashboard will include only metrics that are compatible with GA4. All metrics from data sources other than Google Analytics will be copied over as well.',
			},
			{
				value: MigrationMode.REPLACE_METRICS,
				label: 'Replace metrics on the existing dashboard',
				label2: 'Metrics that are compatible with GA4 will be replace on the existing dashboard. All metrics from data sources other than Google Analytics will be kept as well.',
			},
		];
	}

	get isRemoveIncompatibleMetricsDisabled(): boolean {
		return this.selectedMigrationMode === MigrationMode.CREATE_NEW_BOARD;
	}

	get boardModel(): Board {
		return this.popupData.model;
	}

	get ownerUser(): UserRecord | null {
		return this.boardModel.ownerUser;
	}

	get isProcessingARequest() {
		return this.areMigrationMapsLoading || this.submitInProgress;
	}

	get showSelectionErrors() {
		return !this.isMessageVisible;
	}

	@cached
	get datasourceList() {
		// Filter out only GA4 data sources
		const GA4SourceTypeId = this.mmSourceTypesService.findModelByKey('GoogleAnalytics4')?.id;
		const ga4Sources = this.mmSourcesService.sourceListSpecific.filter(
			(source) => source.type === GA4SourceTypeId,
		);

		const datasources: (SourceListEntry & { disabled?: string })[] = copyObject(ga4Sources);

		// disable sources that have label3 (are in error status)
		datasources.forEach((source) => {
			source.disabled = source.isDisabled ? 'disabled' : '';
		});

		return datasources;
	}

	get areMigrationMapsInvalid(): boolean {
		return this.sourceMigrationMaps.some((migrationMap) => migrationMap.isError);
	}

	get canRequestSetupHelp(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	@action
	triggerSubmitAction(): void {
		if (this.isMessageVisible) {
			if (this.hideMessageNextTime) {
				this.viewSettingsManagerService.save(this.settingsName, { isHidden: true }, true);
			}

			this.isMessageVisible = false;
			this.scheduleReposition();
		} else {
			if (this.areMigrationMapsInvalid) {
				return;
			}

			const migrationMode = this.removeIncompatibleMetrics
					? MigrationMode.REMOVE_METRICS
					: this.selectedMigrationMode,
				mappings = this.sourceMigrationMaps.map((sourceMigrationMap) => {
					return sourceMigrationMap.migrationMap;
				});

			const infolayer = this.infolayerService.viewBoard(this.boardModel);

			if (this.boardModel.numericId) {
				this.mmBoardsService
					.migrateToGA4(this.boardModel.numericId, migrationMode, mappings)
					.then((migrationResponse: MigrationResponse) => {
						infolayer.setDone(
							migrationResponse.board,
							migrationResponse.successfullyMigrated.length,
							migrationResponse.unsuccessfullyMigrated.length,
						);
					})
					.catch((reply: RequestError) => {
						infolayer.setError(reply.errors[0]!.message);
					});
			}

			this.close();
		}
	}

	@action
	openRequestSetupHelp(): void {
		return this.accountService.requestSetupHelp();
	}

	@action
	changeMigrationMode(value: AnySimpleValue) {
		const mode = value as MigrationMode;
		if (mode === MigrationMode.CREATE_NEW_BOARD) {
			this.removeIncompatibleMetrics = false;
		}
		this.selectedMigrationMode = mode;
	}

	@action
	changeRemoveIncompatibleMetrics(value: boolean) {
		this.removeIncompatibleMetrics = value;
	}

	@action
	changeHideMessageNextTime(value: boolean) {
		this.hideMessageNextTime = value;
	}
	<template>
		<div class="title title--vertical">
			<CommonUiIconComponent @name="Sparkles" @size="lg" />
			<span class="text">Universal Analytics to Google Analytics 4 transition helper</span>
		</div>

		<div class="content">

			{{#if this.isMessageVisible}}
				<hr />
				<div class="message">
					Google is officially sunsetting Universal Analytics on July 1, 2023. We’re doing
					everything in our power to support users through this transition to Google
					Analytics 4, including releasing this Google Analytics 4 dashboard transition
					tool.
					<br /><br />
					Based on our testing, approximately 50% of Universal Analytics metrics will be
					automatically transitioned to Google Analytics 4 metrics using this transition
					tool. We will provide a log of metrics that were and were not transitioned via
					email after the transition is complete. Metrics that do not have direct matches
					in Google Analytics 4 will not be automatically transitioned.
					<br /><br />
					Please reach out to our support team if you’d like additional assistance
					updating your dashboards and reporting on Google Analytics 4 data
					{{#unless this.accountService.whiteLabelingEnabled}}
						in Databox
					{{/unless}}. We’re here to help in any way we can.
				</div>

			{{else}}

				<div class="form">
					<div class="board">
						<BoardImagePreviewComponent
							@image={{this.boardModel.image}}
							@color={{this.boardModel.color}}
							@theme={{this.boardModel.bgTheme}}
							@isLazyLoaded={{true}}
						/>
						<div class="board__info">
							<div class="board__title">{{this.boardModel.name}}</div>
							<div class="board__user">
								<AvatarOrInitialsComponent
									@name={{this.ownerUser.name}}
									@avatar={{this.ownerUser.avatar}}
								/>
								<div>{{this.ownerUser.name}}</div>
							</div>
						</div>
					</div>

					<hr />

					<div class="form__label">
						Match Google Analytics Universal Analytics with Google Analytics 4 data
						source(s)
					</div>

					{{#if this.areMigrationMapsLoading}}
						<div class="form__loader">
							<BarLoaderComponent @size="big" />
						</div>
					{{else}}
						<div class="form__rows">
							{{#each this.sourceMigrationMaps as |migrationMap|}}
								<BoardSourceMigrationRowComponent
									@sourceMigrationMap={{migrationMap}}
									@datasourceList={{this.datasourceList}}
									@showSelectionErrors={{this.showSelectionErrors}}
								/>
							{{/each}}
						</div>
					{{/if}}

					<div class="form__label">Transition mode</div>
					<FormRadiobuttonGroupComponent
						class="radiobutton-default"
						@name="migrationMode"
						@selected={{this.selectedMigrationMode}}
						@options={{this.migrationModeOptions}}
						@isDisabled={{this.submitInProgress}}
						@onChange={{this.changeMigrationMode}}
					/>

					<FormCheckboxComponent
						@name="removeIncompatibleMetrics"
						@selected={{this.removeIncompatibleMetrics}}
						@label="Remove Universal Analytics metrics incompatible with GA4 from the dashboard"
						@isDisabled={{this.isRemoveIncompatibleMetricsDisabled}}
						@onChange={{this.changeRemoveIncompatibleMetrics}}
					/>
				</div>

			{{/if}}

			<div class="controls2">

				{{#if this.isMessageVisible}}
					<div class="controls2__left">
						<FormCheckboxComponent
							@name="skipMessage"
							@selected={{this.hideMessageNextTime}}
							@label="Don’t show this message next time"
							@onChange={{this.changeHideMessageNextTime}}
						/>
					</div>
				{{/if}}

				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Start transition"
						@onTrigger={{this.triggerSubmitAction}}
						@submitInProgress={{this.isProcessingARequest}}
					/>
				</div>

			</div>

			{{#unless this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins}}
				<div class="information">
					<div class="information__link">
						<CommonUiIconComponent @name="Book" @size="xl" />
						<div>
							<div class="information__description">Transition to Google Analytics 4</div>
							<a
								href="https://help.databox.com/transitioning-metrics-from-google-universal-analytics-to-google-analytics-4"
								target="_blank"
							>
								Learn more about
							</a>
						</div>
					</div>
					{{#if this.canRequestSetupHelp}}
						<div class="information__link">
							<CommonUiIconComponent @name="Question" @size="xl" />
							<div>
								<div class="information__description">
									Do you need help with the transition?
								</div>
								<a role="button" {{on "click" this.openRequestSetupHelp}}>
									Book a call
								</a>
							</div>
						</div>
					{{/if}}
				</div>
			{{/unless}}

		</div>
	</template>
}
